/**
 * Our Impact: count-up (once per page load, on scroll into view).
 * Displays as 1M → 2M → … → NM, then NM+ when complete.
 */

const ANIMATION_DURATION_MS = 1800;
const START_VALUE = 1;
const OBSERVER_KEY = "_ourImpactObserver";

/**
 * @param {string | undefined} raw
 * @returns {number | null}
 */
const parseCount = (raw) => {
  const value = parseInt(String(raw || "").trim(), 10);
  if (Number.isNaN(value) || value < START_VALUE) return null;
  return value;
};

/**
 * @param {number} value
 * @param {boolean} [withPlus]
 * @returns {string}
 */
const formatStatHtml = (value, withPlus = false) =>
  `${Math.round(value)}<span class="our-impact__stat-unit">M${withPlus ? "+" : ""}</span>`;

/**
 * @param {number} t 0–1
 * @returns {number}
 */
const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

/**
 * @param {HTMLElement} countEl
 */
const animateCta = (countEl) => {
  const endValue = parseCount(countEl.dataset.count);
  if (endValue == null) return;

  const start = performance.now();
  countEl.innerHTML = formatStatHtml(START_VALUE);

  const tick = (now) => {
    const t = Math.min(1, (now - start) / ANIMATION_DURATION_MS);
    const value = START_VALUE + (endValue - START_VALUE) * easeOutQuad(t);
    countEl.innerHTML = formatStatHtml(value);

    if (t < 1) {
      requestAnimationFrame(tick);
      return;
    }

    countEl.innerHTML = formatStatHtml(endValue, true);
  };

  requestAnimationFrame(tick);
};

/**
 * @param {HTMLElement} sectionEl
 */
const playSectionAnimation = (sectionEl) => {
  if (sectionEl.dataset.animated === "true") return;
  sectionEl.dataset.animated = "true";

  sectionEl.querySelectorAll("[js-our-impact-count]").forEach((countEl) => {
    animateCta(countEl);
  });
};

/**
 * @param {HTMLElement} section
 */
const disconnectObserver = (section) => {
  if (section[OBSERVER_KEY]) {
    section[OBSERVER_KEY].disconnect();
    section[OBSERVER_KEY] = null;
  }
};

/**
 * @param {ParentNode} [root]
 */
export const buildOurImpact = (root = document) => {
  const scope = root && root.querySelectorAll ? root : document;
  const sections = scope.querySelectorAll("[js-our-impact]");

  if (!sections.length) return;

  sections.forEach((section) => {
    if (section.dataset.impactReady === "true") return;
    section.dataset.impactReady = "true";

    section.querySelectorAll("[js-our-impact-count]").forEach((countEl) => {
      countEl.innerHTML = formatStatHtml(START_VALUE);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          playSectionAnimation(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: "0px 0px -5% 0px", threshold: 0.15 }
    );

    section[OBSERVER_KEY] = observer;
    observer.observe(section);
  });
};

/**
 * @param {ParentNode} [root]
 */
export const resetOurImpact = (root = document) => {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll("[js-our-impact]").forEach((section) => {
    disconnectObserver(section);
    delete section.dataset.impactReady;
    delete section.dataset.animated;
  });
  buildOurImpact(scope);
};

window.buildOurImpact = buildOurImpact;
window.resetOurImpact = resetOurImpact;

const boot = () => buildOurImpact();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}

document.addEventListener("shopify:section:load", (event) => {
  const root = event.target;
  if (!root?.querySelector?.("[js-our-impact]") && !root?.matches?.("[js-our-impact]")) {
    return;
  }
  resetOurImpact(root);
});
